import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { TransactionFilters } from '../components/TransactionFilters';
import { TransactionDetailsPanel } from '../components/TransactionDetailsPanel';
import { Pagination } from '../components/Pagination';
import { LoadingScreen } from '../components/LoadingScreen';
import { TransactionAPI, ApprovalAPI } from '../services/api';
import { UnifiedTransaction } from '../types/payment';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/rolePermissions';
import { AlertService } from '../services/AlertService';
import { useTransactionFilters, toBackendFilters } from '../hooks/useTransactionFilters';
import './TransactionsTable.css';

const MANAGER_ROLES = ['sales_team_lead', 'processor_team_lead', 'floor_manager', 'director', 'admin'];

export const Analytics: React.FC = () => {
  const { user } = useAuth();
  const canView = !!user?.role && MANAGER_ROLES.includes(user.role);
  const canDecrypt = hasPermission(user?.role, 'canDecryptCardData');

  const [transactions, setTransactions] = useState<UnifiedTransaction[]>([]);
  const [stats, setStats] = useState<{ total: number; total_amount: number }>({ total: 0, total_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'asc' | 'desc' }>({
    field: 'created_at',
    direction: 'desc',
  });
  const [selectedTransaction, setSelectedTransaction] = useState<UnifiedTransaction | null>(null);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [decryptedData, setDecryptedData] = useState<any>(null);
  const [showEncryptedData, setShowEncryptedData] = useState<string | null>(null);

  const { filters, updateFilter, clearFilters, hasActiveFilters, filterStats } = useTransactionFilters(transactions);

  const sortFieldMap: Record<string, string> = {
    amount: 'amount', created_by: 'created_by', tx_type: 'tx_type', nmi_response_code: 'response_code',
  };

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const res = await TransactionAPI.getAnalytics({
        offset: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        sortField: sortFieldMap[sortConfig.field] || 'created_at',
        sortDir: sortConfig.direction,
        filters: toBackendFilters(filters),
      });
      setTransactions(res.items as UnifiedTransaction[]);
      setStats({ total: res.total, total_amount: res.total_amount });
    } catch (error: any) {
      console.error('Failed to load analytics:', error);
      AlertService.error(error.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canView) return;
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, sortConfig, filters]);

  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortConfig]);

  const handleSort = (field: string) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await TransactionAPI.exportAnalyticsCsv(toBackendFilters(filters), {
        field: sortFieldMap[sortConfig.field] || 'created_at',
        dir: sortConfig.direction,
      });
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleViewDetails = (transaction: UnifiedTransaction) => {
    setSelectedTransaction(transaction);
    setShowDetailsPanel(true);
    setDecryptedData(null);
    setShowEncryptedData(null);
  };

  const handleCloseDetails = () => {
    setShowDetailsPanel(false);
    setSelectedTransaction(null);
    setDecryptedData(null);
    setShowEncryptedData(null);
  };

  const handleDecryptInPanel = async (transactionId: string, reason: string) => {
    try {
      const data = await ApprovalAPI.getTransactionEncryptedData(transactionId, true, reason);
      setDecryptedData(data);
      setShowEncryptedData(transactionId);
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to decrypt transaction data');
    }
  };

  // --- Display helpers (kept in sync with the All Transactions page) ---
  const maskCreditCard = (ccNumber?: string, masked?: string): string => {
    if (masked) return masked;
    if (!ccNumber) return '****-****-****-****';
    if (canDecrypt) return ccNumber;
    return ccNumber.length >= 4 ? `****-****-****-${ccNumber.slice(-4)}` : '****-****-****-****';
  };

  const formatCardData = (t: UnifiedTransaction): string => {
    if (canDecrypt && decryptedData && showEncryptedData === t.id) {
      return maskCreditCard(decryptedData.decrypted_card_data?.card_number || '', t.masked_pan);
    }
    if (t.last4) return maskCreditCard(`************${t.last4}`, t.masked_pan);
    return t.masked_pan || '****-****-****-****';
  };

  const getTxTypeDisplayName = (txType: string): string => {
    const map: { [k: string]: string } = {
      sale: 'Sale', auth: 'Auth', validate: 'Validate', capture: 'Capture', void: 'Void', refund: 'Refund',
    };
    return map[txType] || txType.charAt(0).toUpperCase() + txType.slice(1);
  };

  const getStatusColor = (t: UnifiedTransaction): string => {
    if (t.is_voided || t.is_refunded) return 'warning';
    const code = t.nmi_response_code || (t as any).response_code;
    if (code === '100') return 'success';
    if (code) return 'error';
    return 'warning';
  };

  const derivedStatus = (t: UnifiedTransaction): string => {
    if (t.is_voided) return 'Voided';
    if (t.is_refunded) return 'Refunded';
    const code = t.nmi_response_code || (t as any).response_code;
    return code === '100' ? 'Approved' : 'Failed';
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  if (!canView) {
    return (
      <DashboardLayout>
        <div className="payment-history">
          <div className="page-header"><div><h1>Analytics</h1></div></div>
          <div className="empty-state">
            <h3>Access Denied</h3>
            <p>You do not have permission to view this page.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="payment-history">
        <div className="page-header">
          <div>
            <h1>Analytics</h1>
            <p>Every transaction — approved and failed. Voided/refunded sales show on their original row with a badge.</p>
          </div>
          <button className="btn btn-primary" onClick={handleExport} disabled={exporting}>
            {exporting ? 'Exporting…' : '⬇️ Export CSV'}
          </button>
        </div>

        {/* Gate on stats (persists across refetch) not `!loading`, so the cards
            don't blink out on every filter-triggered reload. */}
        {(stats.total > 0 || hasActiveFilters) && (
          <div className="ph-summary">
            <div className="ph-summary-card">
              <div className="ph-summary-number">{stats.total.toLocaleString()}</div>
              <div className="ph-summary-label">Total Transactions</div>
            </div>
            <div className="ph-summary-card">
              <div className="ph-summary-number">{formatCurrency(stats.total_amount)}</div>
              <div className="ph-summary-label">Total Amount</div>
            </div>
          </div>
        )}

        {/* Do NOT gate on `!loading`: filters drive a server refetch (loading=true) on
            every keystroke, and unmounting the panel mid-type collapses it and steals
            focus — limiting you to one character / one filter at a time. */}
        {(transactions.length > 0 || hasActiveFilters) && (
          <TransactionFilters
            filters={filters}
            updateFilter={updateFilter}
            clearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
            transactions={transactions}
            filterStats={filterStats}
            showVoidRefundFilters={true}
            showProcessingStatusFilter={false}
            showSaleFilters={true}
          />
        )}

        {/* Full-screen overlay only on the first load; filter-driven refetches reload
            in the background so the table + filter panel stay visible and interactive. */}
        <LoadingScreen isVisible={loading && transactions.length === 0 && !hasActiveFilters} message="Loading analytics..." />

        {transactions.length > 0 ? (
          <div className="transactions-table-container">
            <div className="table-wrapper">
              <table className="transactions-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('created_by')} className="sortable">
                      Created By
                      {sortConfig.field === 'created_by' && (
                        <span className="sort-indicator">{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </th>
                    <th onClick={() => handleSort('tx_type')} className="sortable">
                      Type
                      {sortConfig.field === 'tx_type' && (
                        <span className="sort-indicator">{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </th>
                    <th>Status</th>
                    <th onClick={() => handleSort('amount')} className="sortable">
                      Amount
                      {sortConfig.field === 'amount' && (
                        <span className="sort-indicator">{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </th>
                    <th>State</th>
                    <th>Category</th>
                    <th>Credit Card</th>
                    <th onClick={() => handleSort('nmi_response_code')} className="sortable">
                      Response Code
                      {sortConfig.field === 'nmi_response_code' && (
                        <span className="sort-indicator">{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </th>
                    <th onClick={() => handleSort('created_at')} className="sortable">
                      Date & Time
                      {sortConfig.field === 'created_at' && (
                        <span className="sort-indicator">{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </th>
                    <th className="actions-column">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction, index) => (
                    <tr key={`${transaction.id}-${index}`}>
                      <td className="created-by-cell">
                        {(transaction as any).created_by_name || transaction.created_by || 'Unknown'}
                      </td>
                      <td className="tx-type-cell">
                        <span className="tx-type-badge">{getTxTypeDisplayName(transaction.tx_type || 'sale')}</span>
                      </td>
                      <td className="status-cell">
                        <span className={`status-badge ${getStatusColor(transaction)}`}>
                          {derivedStatus(transaction)}
                        </span>
                      </td>
                      <td className="amount-cell">
                        <span className="amount-value">
                          {transaction.amount !== undefined && transaction.amount !== null
                            ? `$${Number(transaction.amount).toFixed(2)}`
                            : 'N/A'}
                        </span>
                      </td>
                      <td>{transaction.sale_state || 'N/A'}</td>
                      <td>{transaction.category || 'N/A'}</td>
                      <td className="cc-cell">
                        <div className="cc-info">
                          <div className="cc-number">
                            {formatCardData(transaction)}
                            {transaction.three_ds_status === 'Y' && <span className="badge-3ds">3DS</span>}
                            {transaction.three_ds_status === 'Y-3RI' && <span className="badge-3ds">3RI</span>}
                          </div>
                          {transaction.card_brand && (
                            <div className="cc-brand">{transaction.card_brand.toUpperCase()}</div>
                          )}
                        </div>
                      </td>
                      <td className="response-code-cell">
                        <span className={`response-code ${(transaction.nmi_response_code || (transaction as any).response_code) === '100' ? 'success' : 'error'}`}>
                          {transaction.nmi_response_code || (transaction as any).response_code || 'N/A'}
                          {(transaction.nmi_response_code || (transaction as any).response_code) === '100' && ' ✓'}
                        </span>
                      </td>
                      <td className="date-cell">
                        {new Date(transaction.created_at).toLocaleDateString()}<br />
                        <small>{new Date(transaction.created_at).toLocaleTimeString()}</small>
                      </td>
                      <td className="actions-cell">
                        <div className="action-buttons">
                          <button className="btn btn-outline-secondary btn-sm" onClick={() => handleViewDetails(transaction)}>
                            📋 Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentPage}
              totalItems={stats.total}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
              pageSizeOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        ) : !loading && !hasActiveFilters ? (
          <div className="empty-state">
            <h3>No Transactions</h3>
            <p>No transactions found.</p>
          </div>
        ) : !loading ? (
          <div className="empty-state">
            <h3>No Matching Results</h3>
            <p>No transactions match your current filter criteria.</p>
            <p>Try adjusting your filters or clearing them.</p>
          </div>
        ) : null}

        <TransactionDetailsPanel
          transaction={selectedTransaction}
          isOpen={showDetailsPanel}
          onClose={handleCloseDetails}
          decryptedData={showEncryptedData === selectedTransaction?.id ? decryptedData : null}
          onDecryptData={canDecrypt ? handleDecryptInPanel : undefined}
        />
      </div>
    </DashboardLayout>
  );
};

export default Analytics;
